/** Default BCP 47 tag when callers omit `locale` on these helpers. */
export const DEFAULT_STRING_LOCALE = 'en-US' as const;

/**
 * @fileoverview Generic string manipulation helpers for the frontend.
 *
 * ## Purpose
 * Central place for **reusable** string helpers (casing, truncation, splitting, normalization,
 * etc.) so callers share one implementation and behavior stays consistent. Helpers are **pure when
 * `locale` is passed** (no environment reads). When `locale` is omitted, {@link resolveLocale} uses
 * {@link DEFAULT_STRING_LOCALE}, so those call paths are deterministic for a given build.
 *
 * ## What belongs here
 * - Display-oriented transforms used in multiple features (e.g. name/title casing, ellipsis truncation).
 * - Token-agnostic helpers that only depend on `string` input/output.
 * - Small building blocks that compose well (trim, split, join patterns).
 *
 * ## What does *not* belong here
 * - **Date/time formatting** → `dateFormat.utils.ts` (or similar).
 * - **Domain-specific** strings tied to one API or form (e.g. form codes, claim keys) → feature or
 *   `*Constants.ts` / dedicated helper next to the consumer.
 * - **HTML sanitization** → use the project’s sanitization path, not raw string helpers.
 * - **Heavy i18n** — helpers accept an optional **BCP 47** `locale`; when omitted, they use
 *   {@link DEFAULT_STRING_LOCALE}. Override `locale` in tests or special cases. Full CLDR
 *   linguistic title case is out of scope.
 *
 * ## Conventions for new exports
 * 1. Accept `string | null | undefined` when the value may be missing; return `''` or a documented
 *    sentinel unless a different contract is clearly better.
 * 2. Keep functions **pure given inputs**: no hidden I/O. If the API takes an optional `locale`,
 *    document that omitting it uses {@link DEFAULT_STRING_LOCALE} (pass an explicit locale in tests
 *    or when you need a different tag).
 * 3. Add a **section comment** (`// --- Casing ---`) and **JSDoc** with `@param`, `@returns`, and
 *    a short `@example` when behavior is non-obvious.
 * 4. Prefer **grapheme** segmentation when `Intl.Segmenter` is available; fall back to code-unit
 *    operations in older environments.
 *
 * ## Suggested future additions (examples)
 * - `normalizeWhitespace(value)` — collapse internal spaces, trim ends.
 * - `splitLines(value)` — split on `\r?\n` for multiline display.
 * - `truncateMiddle(value, maxLength)` — ellipsis in the middle of long filenames.
 *
 * @packageDocumentation
 */

// --- Casing ---

/** Uses explicit `locale` when provided; otherwise {@link DEFAULT_STRING_LOCALE}. */
function resolveLocale(explicit: string | undefined): string {
  if (explicit !== undefined && explicit.trim() !== '') {
    return explicit;
  }
  return DEFAULT_STRING_LOCALE;
}

/** Uppercases the first grapheme (when possible) and lowercases the rest, using `locale`. */
function capitalizeWordLocale(word: string, locale: string): string {
  if (!word) return '';
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(locale, { granularity: 'grapheme' });
    let first = true;
    let out = '';
    for (const { segment } of segmenter.segment(word)) {
      out += first ? segment.toLocaleUpperCase(locale) : segment.toLocaleLowerCase(locale);
      first = false;
    }
    return out;
  }
  return word.charAt(0).toLocaleUpperCase(locale) + word.slice(1).toLocaleLowerCase(locale);
}

/**
 * Canonical Roman numeral shape. Section titles only need small ordinals
 * (I–XXXIX); capping avoids treating ordinary words like `MIX` (1009) as Romans.
 */
const ROMAN_NUMERAL_CORE_PATTERN =
  /^(?=[MDCLXVI])M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i;

/** Max Roman value treated as a section ordinal (XXXIX). */
const MAX_SECTION_ROMAN_VALUE = 39;

const ROMAN_CHAR_VALUES: Record<string, number> = {
  I: 1,
  V: 5,
  X: 10,
  L: 50,
  C: 100,
  D: 500,
  M: 1000,
};

const TRAILING_PUNCTUATION_PATTERN = /^(.+?)([.:;,]+)$/;

function splitTrailingPunctuation(word: string): { core: string; suffix: string } {
  const match = word.match(TRAILING_PUNCTUATION_PATTERN);
  if (!match) {
    return { core: word, suffix: '' };
  }
  return { core: match[1] ?? word, suffix: match[2] ?? '' };
}

function romanNumeralToInt(core: string): number | null {
  const upper = core.toUpperCase();
  if (!ROMAN_NUMERAL_CORE_PATTERN.test(upper)) {
    return null;
  }
  let total = 0;
  for (let i = 0; i < upper.length; i++) {
    const current = ROMAN_CHAR_VALUES[upper[i] ?? ''] ?? 0;
    const next = ROMAN_CHAR_VALUES[upper[i + 1] ?? ''] ?? 0;
    total += current < next ? -current : current;
  }
  return total > 0 ? total : null;
}

/** True when `core` is a section-style Roman numeral (1–39, case-insensitive). */
export function isRomanNumeralToken(core: string): boolean {
  if (!core) return false;
  const value = romanNumeralToInt(core);
  return value !== null && value <= MAX_SECTION_ROMAN_VALUE;
}

/**
 * Roman numeral casing for section titles:
 * - all-caps → keep
 * - all-lowercase → keep
 * - mixed / Pascal → uppercase
 */
function applyRomanNumeralCasingPolicy(core: string, locale: string): string {
  const upper = core.toLocaleUpperCase(locale);
  const lower = core.toLocaleLowerCase(locale);
  if (core === upper || core === lower) {
    return core;
  }
  return upper;
}

/**
 * Title-cases one word token for section-style labels.
 * Preserves Roman numeral tokens (including a trailing `.` / `:` / `;` / `,`)
 * per {@link applyRomanNumeralCasingPolicy}; otherwise uses {@link capitalizeWordLocale}.
 */
export function formatTitleCaseWord(word: string, locale?: string): string {
  if (!word) return '';
  const loc = resolveLocale(locale);
  const { core, suffix } = splitTrailingPunctuation(word);
  if (isRomanNumeralToken(core)) {
    return applyRomanNumeralCasingPolicy(core, loc) + suffix;
  }
  return capitalizeWordLocale(word, loc);
}

/**
 * Capitalizes the first grapheme of each whitespace-delimited word (e.g. `"john DOE"` → `"John Doe"`).
 * Use for display text such as names and labels.
 *
 * **Note:** This is *not* programming-language PascalCase for identifiers (`FooBar` with no spaces).
 * For that, join words without spaces at the call site after normalizing tokens.
 *
 * @param value - Raw string; `null`/`undefined`/blank after trim yields `''`.
 * @param locale - Optional **BCP 47** override (e.g. `tr-TR`). Omitted → {@link DEFAULT_STRING_LOCALE}.
 * @returns Title-style words separated by single spaces.
 * @example
 * capitalizeWords('  jane   DOE ') // 'Jane Doe'
 */
export function capitalizeWords(value: string | null | undefined, locale?: string): string {
  if (!value?.trim()) return '';
  const loc = resolveLocale(locale);
  return value
    .trim()
    .split(/\s+/)
    .map((word) => capitalizeWordLocale(word, loc))
    .join(' ');
}

/**
 * Capturing split so `{{...}}` spans stay in the result (MDN: regex capturing groups
 * are included in `String.prototype.split` output).
 */
const PROTECTED_SPAN_SPLIT = /(\{\{[\s\S]*?\}\})/g;

/**
 * Apply `formatUnprotected` to text outside `{{...}}`; emit the inner span unchanged
 * and strip the delimiters from the result.
 */
export function applyWithProtectedSpans(
  value: string,
  formatUnprotected: (segment: string) => string,
): string {
  return value
    .split(PROTECTED_SPAN_SPLIT)
    .map((segment) => {
      if (segment.startsWith('{{') && segment.endsWith('}}') && segment.length >= 4) {
        return segment.slice(2, -2);
      }
      return formatUnprotected(segment);
    })
    .join('');
}

/**
 * Converts ALL CAPS (or mixed) prose to title case for **section-style** labels.
 *
 * - Preserves text in parentheses unchanged (including spelling and internal spacing).
 * - Preserves text inside `{{...}}` unchanged and strips the delimiters.
 * - Title-cases each non-parenthetical word via {@link formatTitleCaseWord} (Roman numerals
 *   keep uniform source casing; mixed Romans become uppercase).
 *
 * @param value - Section title or similar; `null`/`undefined` yields `''`.
 * @param locale - Optional **BCP 47** override. Omitted → {@link DEFAULT_STRING_LOCALE}.
 * @returns Title-cased display string.
 * @example
 * toTitleCase('SECTION I (OPTIONAL)') // 'Section I (OPTIONAL)'
 * toTitleCase('II. DEPLOYMENT INFORMATION (DEP)') // 'II. Deployment Information (DEP)'
 * toTitleCase('REVIEW - {{PHA}} NOTES') // 'Review - PHA Notes'
 */
export function toTitleCase(value: string | null | undefined, locale?: string): string {
  if (value == null) return '';
  const loc = resolveLocale(locale);
  return applyWithProtectedSpans(value, (str) => {
    const segments = str.split(/(\([^)]+\))/g);

    const result = segments.map((segment) => {
      if (segment.startsWith('(') && segment.endsWith(')')) {
        return segment;
      }

      return segment
        .split(' ')
        .map((word) => formatTitleCaseWord(word, loc))
        .join(' ');
    });

    return result.join('');
  });
}

// --- Truncation ---

function hasIntlSegmenter(): boolean {
  return typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function';
}

/**
 * Shortens a string when longer than `maxLength`, appending `ellipsis` (default `…`).
 *
 * When {@link Intl.Segmenter} is available, the visible prefix respects **grapheme** boundaries
 * for `locale`; otherwise falls back to UTF-16 code units. Total output length is still measured in
 * **code units** (same as `String.length`).
 *
 * @param value - Text to shorten; `null`/`undefined` yields `''`.
 * @param maxLength - Maximum length of the **result** string (including ellipsis when truncated).
 * @param ellipsis - Suffix when truncated; default is Unicode ellipsis `…`.
 * @param locale - Optional **BCP 47** tag for `Intl.Segmenter` grapheme breaks. Omitted → {@link DEFAULT_STRING_LOCALE}.
 * @returns Original string if it fits; otherwise prefix + ellipsis. If `maxLength` is smaller than
 *          `ellipsis.length`, returns a clipped ellipsis only.
 * @example
 * truncateWithEllipsis('Hello world', 8) // 'Hello w…'
 */
export function truncateWithEllipsis(
  value: string | null | undefined,
  maxLength: number,
  ellipsis: string = '…',
  locale?: string,
): string {
  if (value == null) return '';
  const s = value;
  const loc = resolveLocale(locale);
  if (maxLength < 1) return '';
  const ellipsisLen = ellipsis.length;
  if (maxLength <= ellipsisLen) {
    return ellipsis.slice(0, maxLength);
  }
  if (s.length <= maxLength) return s;

  const budget = maxLength - ellipsisLen;
  if (budget < 1) {
    return ellipsis.slice(0, maxLength);
  }

  if (hasIntlSegmenter()) {
    const segmenter = new Intl.Segmenter(loc, { granularity: 'grapheme' });
    let prefix = '';
    for (const { segment } of segmenter.segment(s)) {
      if (prefix.length + segment.length > budget) {
        break;
      }
      prefix += segment;
    }
    if (prefix.length === 0 && s.length > 0) {
      return ellipsis.slice(0, maxLength);
    }
    return prefix + ellipsis;
  }

  return s.slice(0, budget) + ellipsis;
}
